import { Injectable, signal, computed } from '@angular/core';
import { TreeType, TreeLevel } from './tree.model';

@Injectable({
  providedIn: 'root'
})


export class DragDropService {
  // Map of [Type -> [Level -> List of IDs]]
  private connectedLists = signal<Map<TreeType, Map<TreeLevel, string[]>>>(new Map());

  // Track the type of the tree currently being hovered
  hoveredType = signal<TreeType | null>(null);

  // Track the level and type of the item currently being dragged
  draggedLevel = signal<TreeLevel | null>(null);
  draggedType = signal<TreeType | null>(null);
  draggedParentTag = signal<string | null>(null);

  // Exposed signal for components to read connections for a specific level and type
  getConnectedLists(level: TreeLevel, type: TreeType) {
    return computed(() => {
        const map = this.connectedLists();
        const myLists = map.get(type)?.get(level) || [];
        
        // If type is 'source', it can also connect to 'target' lists of same level
        if (type === TreeType.Source) {
            const targetLists = map.get(TreeType.Target)?.get(level) || [];
            return [...myLists, ...targetLists];
        }
        
        // If type is 'target', it can only connect to 'target' lists (and itself)
        return myLists;
    });
  }
  
  startDrag(level: TreeLevel, type: TreeType, parentTag?: string) {
      this.draggedLevel.set(level);
      this.draggedType.set(type);
      this.draggedParentTag.set(parentTag ?? null);
  }
  
  endDrag() {
      this.draggedLevel.set(null);
      this.draggedType.set(null);
      this.draggedParentTag.set(null);
      this.hoveredType.set(null);
  }

  setHoveredType(type: TreeType) {
      this.hoveredType.set(type);
  }

  register(id: string, level: TreeLevel, type: TreeType) {
    const currentMap = new Map(this.connectedLists());
    
    // Get or create type map
    let typeMap = currentMap.get(type);
    if (!typeMap) {
        typeMap = new Map<TreeLevel, string[]>();
        currentMap.set(type, typeMap);
    }
    
    // Get or create level list
    const existing = typeMap.get(level) || [];
    if (!existing.includes(id)) {
        typeMap.set(level, [...existing, id]);
        this.connectedLists.set(currentMap);
    }
  }

  unregister(id: string, level: TreeLevel, type: TreeType) {
    const currentMap = new Map(this.connectedLists());
    const typeMap = currentMap.get(type);
    
    if (typeMap) {
        const existing = typeMap.get(level) || [];
        const newLists = existing.filter(listId => listId !== id);
        if (newLists.length > 0) {
            typeMap.set(level, newLists);
        } else {
            typeMap.delete(level);
        }
        
        // Cleanup type map if empty? Optional.
        this.connectedLists.set(currentMap);
    }
  }
}
